import Image from "next/image";
import Link from "next/link";
import type { ReactNode } from "react";

import { LogoOI } from "@/components/common/LogoOI";
import { Icona, type NomeIcona } from "@/components/home/Icona";
import { DATI_MONO, ETICHETTA, ETICHETTA_ACCESA, RIMANDO, TITOLO_LINK } from "@/components/home/stile";
import { SegnoTitolo, TestaSezione } from "@/components/home/TestaSezione";
import { FUOCO } from "@/components/ui/pulsanti";
import { ETICHETTA_VISTA, type DatiCaso } from "@/lib/caso";
import { PRIORITY_MAP } from "@/lib/constants";
import { risolviLuoghi } from "@/lib/mappaLuoghi";
import { etichettaLuogo } from "@/lib/nomiLuoghi";
import type { Approfondimento, RulliNotizia, VoceRullo } from "@/lib/notizia";
import { paeseDelLuogo } from "@/lib/paeseDelLuogo";
import { TONO_ICONA, iconaCategoria } from "@/lib/prima-pagina";
import { isStrictCountry } from "@/lib/regioni";
import { slugify } from "@/lib/slug";
import { areaDi, servizioDi } from "@/lib/sorteggio-servizi";
import type { EntityKind, NewsItem } from "@/lib/types";

/* ───────────────────────────────────────────────────────────────
   I blocchi della notizia aperta sul modello del 19/10/2026
   (uploads/notizia-aperta.html + uploads/ISTRUZIONI-notizia-aperta.md,
   esempio OI-353618 «Minth in Serbia»). Li rende il ramo `item.free` di
   DispaccioView; il riservato non passa di qui.

   Nessun modulo "use client": li importa DispaccioView, che lo è, e
   nessuno di questi pezzi ha stato. «Mostra tutte» e i gruppi di «Chi e
   dove» funzionano col solo CSS (`<details>`, una casella e `:has`, il
   blocco `.oi-na-*` di globals.css), come chiede il §4 delle istruzioni.

   Dati: tutto da `NewsItem`, `DatiCaso` e dalle richieste della rotta.
   Restano fuori, perché a monte non esistono (verificato il 19/10/2026
   sui 14 dispacci aperti del rullo: `indicator` vuoto, `indicator_ai`
   nullo, nessun campo di didascalia né di cifra):
   - «Quanto» di «In breve»;
   - il riquadro «L'Osservatorio · Perché ti riguarda»;
   - la didascalia della foto (resta il solo credito);
   - i sottotitoli scritti dalla redazione: la descrizione arriva come
     testo piano, e un sottotitolo si riconosce solo se è un blocco di
     una riga breve senza punto finale (`blocchiDelCorpo`), cosa che oggi
     non capita su nessun aperto.
   ─────────────────────────────────────────────────────────────── */

/* ── Indirizzi ──────────────────────────────────────────────────── */

/** Gli indirizzi di filtro di /ricerca, come nel resto del sito. */
export function hrefEntita(tipo: EntityKind, nome: string): string {
  if (tipo === "places") return `/rullo?place=${encodeURIComponent(nome)}`;
  return `/rullo?etype=${encodeURIComponent(tipo)}&entity=${encodeURIComponent(nome)}`;
}

/** La scheda del Paese, con lo slug calcolato dal titolo come in
 *  lib/rullo-giorno.ts e lib/prima-pagina.ts. */
export function hrefPaese(paese: string): string {
  return `/paesi/${slugify(paese)}`;
}

/** Il tono del tema di una rubrica, o il mare se la rubrica è vuota (a
 *  monte lo è spesso). */
function tonoRubrica(categoria: string): string {
  return categoria ? TONO_ICONA[iconaCategoria(categoria)] : "var(--color-mare)";
}

/* ── Il corpo ───────────────────────────────────────────────────── */

export type BloccoCorpo = { tipo: "p" | "h3"; testo: string };

/** Un sottotitolo è un blocco di una sola riga, breve e senza punto
 *  finale: la sola forma in cui a monte potrebbe arrivare un `h3` in un
 *  testo piano. */
function sottotitolo(b: string): boolean {
  return !b.includes("\n") && b.length <= 90 && b.split(/\s+/).length >= 2 && !/[.!?…:;,»"”)]$/.test(b);
}

/** Quanto deve essere lungo, almeno, un capoverso composto da noi quando
 *  il testo a monte arriva senza a capo: le frasi si accumulano finché il
 *  capoverso supera questa misura (circa quattro righe a 760 di misura). */
const CAPOVERSO_MIN = 420;

/** Il corpo in blocchi, più i sottotitoli.
 *
 *  I capoversi sono quelli del testo a monte: un a capo, singolo o doppio,
 *  chiude un capoverso (sulla 6083 la `description` ne ha sei, tutti
 *  singoli). Fino al 20/10/2026 contavano solo gli a capo doppi e, senza,
 *  il testo si spezzava ogni due frasi: un vuoto dopo ogni periodo, che al
 *  committente «sta male». Quando a monte non c'è nessun a capo (il
 *  352992 è un blocco solo) le frasi si raggruppano in capoversi di
 *  almeno `CAPOVERSO_MIN` caratteri. Un sottotitolo in coda, senza testo
 *  sotto, torna capoverso. */
export function blocchiDelCorpo(body: string): BloccoCorpo[] {
  const testo = body.trim();
  if (!testo) return [];
  const conAcapo = /\n/.test(testo);
  const grezzi = conAcapo ? testo.split(/\s*\n\s*/).map((p) => p.trim()).filter(Boolean) : [testo];
  const out: BloccoCorpo[] = [];
  grezzi.forEach((b, i) => {
    if (i > 0 && i < grezzi.length - 1 && sottotitolo(b)) {
      out.push({ tipo: "h3", testo: b });
      return;
    }
    if (conAcapo) {
      out.push({ tipo: "p", testo: b });
      return;
    }
    const frasi = b
      .split(/(?<=[.!?…])\s+(?=[A-ZÀ-ÜŠŽ])/)
      .map((f) => f.trim())
      .filter(Boolean);
    let corrente = "";
    for (const f of frasi) {
      corrente = corrente ? `${corrente} ${f}` : f;
      if (corrente.length >= CAPOVERSO_MIN) {
        out.push({ tipo: "p", testo: corrente });
        corrente = "";
      }
    }
    // Una coda corta si attacca al capoverso prima invece di restare sola.
    if (corrente) {
      const ultimo = out[out.length - 1];
      if (ultimo && ultimo.tipo === "p" && corrente.length < CAPOVERSO_MIN / 2) ultimo.testo += ` ${corrente}`;
      else out.push({ tipo: "p", testo: corrente });
    }
  });
  return out;
}

/** Il sommario di una notizia aperta: la prima frase del primo capoverso
 *  (modello `uploads/notizia-zijin.html` del 20/10/2026, dove sotto il
 *  titolo sta una frase sola e il resto del capoverso apre il corpo).
 *  `resto` è quello che avanza del capoverso, o `null`. Con una frase
 *  sola il capoverso è tutto sommario, come prima. Il taglio è quello di
 *  `blocchiDelCorpo` (fine frase seguita da una maiuscola), quindi una
 *  sigla puntata in mezzo alla frase non lo fa scattare. */
export function primaFrase(capoverso: string): { frase: string; resto: string | null } {
  const [frase, ...altre] = capoverso
    .split(/(?<=[.!?…])\s+(?=[A-ZÀ-ÜŠŽ])/)
    .map((s) => s.trim())
    .filter(Boolean);
  return { frase: frase ?? capoverso, resto: altre.length ? altre.join(" ") : null };
}

/** `p.t` e `h3.int` del modello: serif 20 su 1,75 e sottotitoli serif 26,
 *  a 760 di misura. Il maiuscoletto sulla prima riga del primo capoverso
 *  (modello del 19/10) è uscito col modello Zijin del 20/10/2026, che lo
 *  spegne apposta (`.primo::first-line{font-variant:normal}`). */
export function CorpoNotizia({ blocchi }: { blocchi: BloccoCorpo[] }) {
  if (blocchi.length === 0) return null;
  return (
    <div className="mb-7 max-w-[760px] font-serif text-[20px] leading-[1.75] text-ink">
      {blocchi.map((b, i) =>
        b.tipo === "h3" ? (
          <h3 key={i} className="m-0 mb-3.5 mt-9 font-serif text-[26px] font-normal leading-[1.25] text-ink">
            {b.testo}
          </h3>
        ) : (
          <p key={i} className="m-0 mb-6 last:mb-0">
            {b.testo}
          </p>
        )
      )}
    </div>
  );
}

/* ── La spalla ──────────────────────────────────────────────────── */

/** «In breve» (`.n-dx .n-fatti`): Dove, Chi, Settore. Il modello ha anche
 *  «Quanto» (300 ettari): a monte non c'è un campo così e una cifra non si
 *  inventa, quindi la voce non si rende. Il «Lettura» che la sostituiva
 *  fino al 18/10 è uscito: i minuti stanno già nella riga della firma.
 *  Con un numero dispari di voci l'ultima prende le due colonne. */
export function InBreveAperto({ item, paese }: { item: NewsItem; paese: string | null }) {
  const e = item.entities;
  const chi = [...(e.companies ?? []), ...(e.institutions ?? []), ...(e.persons ?? [])];
  const dove = paese ? etichettaLuogo(paese) : e.places?.[0] ? etichettaLuogo(e.places[0]) : null;
  const fatti = [
    dove ? { k: "Dove", v: dove } : null,
    chi.length > 0 ? { k: "Chi", v: chi.slice(0, 2).join(", ") } : null,
    item.category.trim() ? { k: "Settore", v: item.category.trim() } : null,
  ].filter((f): f is { k: string; v: string } => f !== null);
  if (fatti.length === 0) return null;
  return (
    <section aria-labelledby="in-breve">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 border-b border-ink pb-2.5">
        <h2 id="in-breve" className="m-0 font-serif text-[26px] font-normal leading-[1.15] text-ink">
          <SegnoTitolo />
          In breve
        </h2>
      </div>
      <dl className="m-0 -mt-2 grid grid-cols-2">
        {fatti.map((f, i) => {
          const pari = i % 2 === 1;
          const intera = i === fatti.length - 1 && fatti.length % 2 === 1;
          return (
            <div
              key={f.k}
              className={`border-b border-rule py-3.5 ${intera ? "col-span-2" : pari ? "border-l pl-4" : "pr-3.5"}`}
            >
              <dt className="mb-1 font-sans text-voce text-sommesso">{f.k}</dt>
              <dd className="m-0 font-serif text-lettura leading-[1.3] text-ink">{f.v}</dd>
            </div>
          );
        })}
      </dl>
    </section>
  );
}

/** Quanti nomi per gruppo si vedono prima di «Mostra tutte» (istruzioni,
 *  §3: «la prima dozzina va senza `x`»). */
const NOMI_VISIBILI = 12;

type Gruppo = {
  chiave: string;
  etichetta: string;
  icona: NomeIcona;
  tipo: EntityKind;
  nomi: string[];
  colonne: 1 | 2;
};

function ElencoNomi({ nomi, tipo, colonne, conPaese = false }: {
  nomi: string[];
  tipo: EntityKind;
  colonne: 1 | 2;
  conPaese?: boolean;
}) {
  return (
    <ul className={`m-0 grid list-none gap-x-4 p-0 ${colonne === 2 ? "grid-cols-2" : "grid-cols-1"}`}>
      {nomi.map((n, i) => (
        <li key={n} className={`min-w-0 ${i >= NOMI_VISIBILI ? "oi-na-x" : ""}`}>
          <Link
            href={hrefEntita(tipo, n)}
            className={`group/n flex items-baseline gap-2.5 py-1.5 font-sans text-sommario leading-[1.3] text-ink no-underline transition-colors hover:text-mare max-md:py-2 ${FUOCO}`}
          >
            {/* Il rombo blu, rame al passaggio: 6 pixel pieni invece dei 5
                al 55% del modello, che si leggevano come un pallino grigio. */}
            <span
              aria-hidden
              className="size-[6px] shrink-0 -translate-y-0.5 rotate-45 bg-mare transition-colors group-hover/n:bg-segnale"
            />
            <span className="min-w-0 [overflow-wrap:anywhere]">
              {n}
              {conPaese && paeseDelLuogo(n) ? (
                <small className="ml-1.5 font-sans text-voce text-sommesso">{paeseDelLuogo(n)}</small>
              ) : null}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
}

/** Un gruppo `.n-grp`: `<details>` aperto, e sopra i dodici nomi la
 *  casella «Mostra tutte» che con `:has` scopre gli altri, senza script. */
function GruppoNomi({ g, coda, conto }: { g: Gruppo; coda?: ReactNode; conto?: string }) {
  const nascoste = g.nomi.length - NOMI_VISIBILI;
  return (
    <details open id={`g-${g.chiave}`} className="oi-na-grp group/g scroll-mt-6 border-b border-rule">
      <summary
        className={`flex cursor-pointer list-none items-center gap-2.5 py-3.5 font-sans text-corpo font-semibold text-ink [&::-webkit-details-marker]:hidden ${FUOCO}`}
      >
        <Icona nome={g.icona} className="size-5 text-mare" />
        {g.etichetta}
        <span className="ml-auto font-mono text-meta font-normal tabular-nums text-sommesso">{conto ?? g.nomi.length}</span>
        <span
          aria-hidden
          className="ml-1 mr-0.5 -mt-1 size-[7px] rotate-45 border-b-[1.5px] border-r-[1.5px] border-sommesso transition-transform group-open/g:mt-1 group-open/g:-rotate-[135deg]"
        />
      </summary>
      <ElencoNomi nomi={g.nomi} tipo={g.tipo} colonne={g.colonne} />
      {nascoste > 0 ? (
        <label className="oi-na-piu relative mb-3.5 mt-1 inline-block cursor-pointer font-sans text-voce font-semibold text-mare">
          <input type="checkbox" className="sr-only" />
          <span className="oi-na-tutti">
            Mostra tutte ({g.nomi.length}) <span aria-hidden>↓</span>
          </span>
          <span className="oi-na-meno">
            Mostra meno <span aria-hidden>↑</span>
          </span>
        </label>
      ) : (
        <div className="h-3" />
      )}
      {coda}
    </details>
  );
}

/** «Chi e dove» (`.n-conti` più i gruppi `.n-grp`). I luoghi si dividono
 *  in paesi (quelli che la mappa sa disegnare) e luoghi citati (città,
 *  regioni), e la riga del gruppo li scrive «4 + 8» come il modello. */
export function ChiEDoveAperto({ item }: { item: NewsItem }) {
  const e = item.entities;
  const luoghi = [...new Set((e.places ?? []).map(etichettaLuogo).filter((l) => isStrictCountry(l)))];
  const paesi: string[] = [];
  const citati: string[] = [];
  for (const l of luoghi) (risolviLuoghi([l]).mentionedISO.length > 0 ? paesi : citati).push(l);

  const istituzioni = [...(e.institutions ?? []), ...(e.organizations ?? []), ...(e.parties ?? [])];
  const gruppi: Gruppo[] = (
    [
      { chiave: "per", etichetta: "Persone", icona: "persona", tipo: "persons", nomi: e.persons ?? [], colonne: 2 },
      { chiave: "az", etichetta: "Aziende", icona: "azienda", tipo: "companies", nomi: e.companies ?? [], colonne: 2 },
      {
        chiave: "ist",
        etichetta: "Istituzioni e organizzazioni",
        icona: "istituzione",
        tipo: "institutions",
        nomi: istituzioni,
        colonne: 1,
      },
    ] satisfies Gruppo[]
  ).filter((g) => g.nomi.length > 0);

  type Conto = { chiave: string; etichetta: string; icona: NomeIcona; n: number };
  const conti = ([
    { chiave: "per", etichetta: "Persone", icona: "persona", n: e.persons?.length ?? 0 },
    { chiave: "az", etichetta: "Aziende", icona: "azienda", n: e.companies?.length ?? 0 },
    { chiave: "ist", etichetta: "Istituzioni", icona: "istituzione", n: istituzioni.length },
    { chiave: "pae", etichetta: "Paesi", icona: "paese", n: paesi.length + citati.length },
  ] satisfies Conto[]).filter((c) => c.n > 0);

  return (
    <section aria-labelledby="chi-e-dove">
      <div className="mb-2 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 border-b border-ink pb-2.5">
        <h2 id="chi-e-dove" className="m-0 font-serif text-[26px] font-normal leading-[1.15] text-ink">
          <SegnoTitolo />
          Chi e dove
        </h2>
        <span className={ETICHETTA}>Ogni nome apre l&rsquo;archivio</span>
      </div>
      {conti.length === 0 ? (
        <p className="m-0 mt-3 font-sans text-sommario text-sommesso">
          Il dispaccio non cita persone, aziende, istituzioni o luoghi.
        </p>
      ) : (
        <>
          <nav
            aria-label="Riepilogo delle entità"
            className="mb-1 mt-3.5 grid rounded-[2px] border border-rule bg-surface"
            style={{ gridTemplateColumns: `repeat(${conti.length}, minmax(0, 1fr))` }}
          >
            {conti.map((c, i) => (
              <a
                key={c.chiave}
                href={`#g-${c.chiave}`}
                className={`flex flex-col items-center gap-0.5 px-1 py-2.5 text-ink no-underline transition-colors hover:bg-petrolio ${
                  i > 0 ? "border-l border-rule" : ""
                } ${FUOCO}`}
              >
                <Icona nome={c.icona} className="mb-1 size-5 text-mare" />
                <b className="font-serif text-[26px] font-normal leading-none">{c.n}</b>
                <span className="font-sans text-voce text-sommesso">{c.etichetta}</span>
              </a>
            ))}
          </nav>
          {gruppi.map((g) => (
            <GruppoNomi key={g.chiave} g={g} />
          ))}
          {paesi.length + citati.length > 0 ? (
            <GruppoNomi
              g={{
                chiave: "pae",
                etichetta: "Paesi e luoghi",
                icona: "paese",
                tipo: "places",
                nomi: paesi.length > 0 ? paesi : citati,
                colonne: 2,
              }}
              conto={paesi.length > 0 && citati.length > 0 ? `${paesi.length} + ${citati.length}` : undefined}
              coda={
                paesi.length > 0 && citati.length > 0 ? (
                  <div className="-mt-1 mb-3.5 border-t border-dashed border-rule pt-3">
                    <p className="m-0 mb-1 flex items-center justify-between">
                      <span className={`inline-flex items-center gap-1.5 ${ETICHETTA}`}>
                        <Icona nome="luogo" className="size-[18px] text-mare" />
                        Luoghi citati
                      </span>
                      <span className="font-mono text-meta text-sommesso">{citati.length}</span>
                    </p>
                    <ElencoNomi nomi={citati} tipo="places" colonne={2} conPaese />
                  </div>
                ) : null
              }
            />
          ) : null}
        </>
      )}
    </section>
  );
}

/* ── Il caso ────────────────────────────────────────────────────── */

/** I quattro servizi di «Cosa possiamo fare per te». Fino al 19/10/2026
 *  erano quattro fissi (monitoraggio, due diligence, analisi, sanzioni),
 *  ognuno appaiato a un passo del metodo («01 · Osservare»…). Da allora li
 *  sorteggia la pagina, uno per domanda di /servizi (`sorteggiaPerArea`),
 *  e la card prende la grammatica di «Strumenti per decidere» in prima
 *  pagina: numero e area, titolo, la domanda dell'area in corsivo, la
 *  descrizione e la frase del catalogo come invito. Un passo del metodo
 *  appaiato a un servizio qualsiasi non vorrebbe più dire niente, e una
 *  domanda per area non si ripete fra le quattro card. */
function cardServizi(slugs: string[]) {
  return slugs.flatMap((slug) => {
    const s = servizioDi(slug);
    const area = areaDi(slug);
    if (!s || !area) return [];
    return [
      {
        slug,
        passo: `${s.numero} · ${area.nome}`,
        titolo: s.titolo,
        domanda: `«${area.domanda}»`,
        testo: s.descrizione,
        icona: s.icona,
        invito: s.frase,
      },
    ];
  });
}

const METODO = [
  { n: "01", titolo: "Osservare", testo: "Leggiamo ogni giorno i dispacci locali, in lingua originale." },
  { n: "02", titolo: "Verificare", testo: "Controlliamo registri, atti e documenti ufficiali." },
  { n: "03", titolo: "Valutare", testo: "Mettiamo in fila soggetti, luoghi e date in un solo fascicolo." },
  { n: "04", titolo: "Orientarsi", testo: "Diciamo cosa cambia per le imprese italiane." },
];

/** Quante tappe stanno sulla traccia: le ultime, cioè le più vicine al
 *  dispaccio. Le precedenti stanno nel `<details>` sotto. */
const TAPPE_TRACCIA = 4;

type Tappa = { id: string; titolo: string; href: string | null; data: { iso: string; testo: string }; time: number };

/** Il tempo fra due tappe, sulla traccia (`.n-gap` del modello). Legge
 *  solo le date del filone, quindi server e browser scrivono lo stesso. */
function intervallo(daMs: number, aMs: number): string {
  const giorni = Math.round(Math.abs(aMs - daMs) / 86_400_000);
  if (giorni === 0) return "stesso giorno";
  if (giorni === 1) return "1 giorno";
  if (giorni < 60) return `${giorni} giorni`;
  return `${Math.round(giorni / 30)} mesi`;
}

/** La traccia delle tappe (`.n-traccia` / `.n-tappa`, definite nel CSS
 *  del modello e lasciate fuori dal suo HTML): un filo nel mare, un
 *  rombo per tappa, «Sei qui» sul dispaccio che si sta leggendo e, fra
 *  una tappa e l'altra, quanto tempo è passato. Prende il posto della
 *  cronologia verticale nella colonna del testo, che il modello non ha. */
function TracciaTappe({ item, caso }: { item: NewsItem; caso: DatiCaso }) {
  const tappe: Tappa[] = [
    ...caso.collegati.map((c) => ({ id: c.id, titolo: c.titolo, href: c.href, data: c.data, time: c.time })),
    { id: item.id, titolo: item.title, href: null, data: caso.questo, time: item.time },
  ].sort((a, b) => a.time - b.time);
  const precedenti = tappe.slice(0, Math.max(0, tappe.length - TAPPE_TRACCIA));
  const visibili = tappe.slice(precedenti.length);
  return (
    <>
      <ol
        className="oi-na-traccia m-0 list-none p-0"
        style={{ ["--tappe" as string]: visibili.length }}
        aria-label="Le tappe del filone"
      >
        {visibili.map((t, i) => {
          const qui = t.href === null;
          const dopo = visibili[i + 1];
          const corpo = (
            <>
              <time dateTime={t.data.iso} className={`font-mono text-meta ${qui ? "text-segnale" : "text-sommesso"}`}>
                {t.data.testo}
              </time>
              <b className="font-serif text-[20px] font-normal leading-[1.3]">{t.titolo}</b>
              <span className="mt-auto flex items-center gap-1.5 border-t border-rule pt-3 font-sans text-voce text-sommesso">
                {qui ? (
                  <>
                    <LogoOI variant="segno" decorative accento="fill-segnale" className="h-3 w-auto text-mare" />
                    Questo dispaccio
                  </>
                ) : (
                  <>
                    Leggi la tappa <span aria-hidden>↗</span>
                  </>
                )}
              </span>
            </>
          );
          return (
            <li key={t.id} className="relative flex min-w-0">
              {qui ? (
                <span aria-hidden className="oi-na-qui font-sans text-voce font-semibold text-segnale">
                  Sei qui
                </span>
              ) : null}
              {qui ? (
                <div aria-current="page" className="oi-na-tappa oi-na-ora">
                  {corpo}
                </div>
              ) : (
                <Link href={t.href!} className={`oi-na-tappa no-underline ${FUOCO}`}>
                  {corpo}
                </Link>
              )}
              {dopo ? (
                <span className="oi-na-intervallo font-mono text-meta text-sommesso">
                  {intervallo(t.time, dopo.time)}
                </span>
              ) : null}
            </li>
          );
        })}
      </ol>
      {precedenti.length > 0 ? (
        <details className="group/p -mt-2">
          <summary
            className={`inline-flex cursor-pointer list-none items-center gap-1.5 font-sans text-corpo font-semibold text-mare [&::-webkit-details-marker]:hidden ${FUOCO}`}
          >
            <span className="group-open/p:hidden">
              Le {precedenti.length === 1 ? "tappa precedente" : `${precedenti.length} tappe precedenti`}{" "}
              <span aria-hidden>↓</span>
            </span>
            <span className="hidden group-open/p:inline">
              Nascondi le tappe precedenti <span aria-hidden>↑</span>
            </span>
          </summary>
          <ol className="m-0 mt-3 list-none border-t border-rule p-0">
            {precedenti.map((t) => (
              <li key={t.id} className="border-b border-rule">
                <Link
                  href={t.href!}
                  className={`grid grid-cols-1 gap-x-6 gap-y-1 py-3 text-ink no-underline transition-colors hover:text-mare sm:grid-cols-[200px_minmax(0,1fr)] ${FUOCO}`}
                >
                  <time dateTime={t.data.iso} className="font-mono text-meta text-sommesso">
                    {t.data.testo}
                  </time>
                  <span className="font-serif text-lettura leading-[1.35]">{t.titolo}</span>
                </Link>
              </li>
            ))}
          </ol>
        </details>
      ) : null}
    </>
  );
}

/** «Come ci si è arrivati» (`.zj-seq` del modello Zijin del 20/10/2026):
 *  il filone in una colonna, dentro il testo, al posto della fascia «Il
 *  caso». Lo usano le versioni storiche (/article/history/…), dove la
 *  fascia a tutta larghezza con servizi e cifre era troppo per una tappa
 *  passata. Tutte le tappe, dalla più vecchia, in forma di registro
 *  (richiesta del 20/10/2026: «una questione più tecnica», non
 *  d'immagine): testata con tappe, arco in giorni e date estreme, colonne
 *  N. / Data / Δ (giorni dalla tappa prima) / Tappa, filetti fra le righe;
 *  restano i rombi numerati sul filo e i titoli. La tappa letta in rame e
 *  senza collegamento, le altre portano alla loro pagina. Sotto 900 data e
 *  Δ stanno su una riga sopra il titolo. */
export function ComeSiEArrivati({ item, caso }: { item: NewsItem; caso: DatiCaso }) {
  if (caso.vista === "notizia") return null;
  const tappe: Tappa[] = [
    ...caso.collegati.map((c) => ({ id: c.id, titolo: c.titolo, href: c.href, data: c.data, time: c.time })),
    { id: item.id, titolo: item.title, href: null, data: caso.questo, time: item.time },
  ].sort((a, b) => a.time - b.time);
  const prima = tappe[0];
  const ultima = tappe[tappe.length - 1];
  // Giorni interi fra due tappe: solo date del filone, quindi server e
  // browser contano uguale.
  const giorni = (a: number, b: number) => Math.round((b - a) / 86_400_000);
  const arco = giorni(prima.time, ultima.time);
  const COLONNE =
    "grid grid-cols-[28px_minmax(0,1fr)] gap-x-3.5 min-[901px]:grid-cols-[28px_150px_64px_minmax(0,1fr)]";
  return (
    <section id="caso" aria-labelledby="come-arrivati" className="oi-na-seq mt-[34px] max-w-[760px] scroll-mt-6">
      {/* Testata del registro: nome a sinistra, a destra i numeri del
          filone (tappe, arco in giorni, prima e ultima data). */}
      <div className="flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1 border-b border-ink px-5 py-3">
        <p id="come-arrivati" className={`m-0 ${ETICHETTA_ACCESA}`}>
          Come ci si è arrivati
        </p>
        <p className={`m-0 ${DATI_MONO}`}>
          {tappe.length} tappe · {arco} giorni · {prima.data.testo} → {ultima.data.testo}
        </p>
      </div>
      <div aria-hidden className={`${COLONNE} border-b border-rule px-5 py-2 ${DATI_MONO} max-[900px]:hidden`}>
        <span>N.</span>
        <span>Data</span>
        <span>Δ</span>
        <span>Tappa</span>
      </div>
      <ol className="m-0 list-none p-0">
        {tappe.map((t, i) => {
          const qui = t.href === null;
          const delta = i === 0 ? "—" : `+${giorni(tappe[i - 1].time, t.time)} g`;
          return (
            <li
              key={t.id}
              className={`relative ${COLONNE} items-baseline gap-y-1 border-b border-rule px-5 py-2.5 last:border-b-0 ${qui ? "oi-na-ora" : ""}`}
            >
              <span aria-hidden className="oi-na-rombo self-start font-mono text-[10.5px] font-semibold">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="font-mono text-meta uppercase tracking-dato max-[900px]:flex max-[900px]:gap-2">
                <time dateTime={t.data.iso} className={qui ? "text-segnale" : "text-ink"}>
                  {t.data.testo}
                </time>
                <span className="text-sommesso min-[901px]:hidden">· {delta}</span>
              </span>
              <span className="font-mono text-meta tabular-nums text-sommesso max-[900px]:hidden">{delta}</span>
              <p className="col-start-2 m-0 font-serif text-[16px] leading-[1.4] text-ink min-[901px]:col-start-4">
                {qui ? (
                  <span aria-current="page">
                    {t.titolo}{" "}
                    <span className="font-sans text-voce font-semibold text-segnale">· questa versione</span>
                  </span>
                ) : (
                  <Link href={t.href!} className={TITOLO_LINK}>
                    {t.titolo}
                  </Link>
                )}
              </p>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/** «Il caso» (`.n-caso` + `.n-duo`): la fascia tinta a tutta larghezza
 *  con la testata col segno, la traccia delle tappe e il riquadro a due
 *  metà, i quattro servizi a sinistra e a destra le cifre del filone
 *  (contate in lib/caso-dati.ts) col metodo in quattro passi. Solo per
 *  casi e report: una notizia non ha un lavoro da raccontare.
 *
 *  Il modello ha anche «Tutto il caso ↗»: non c'è, perché una pagina del
 *  caso non esiste (lo stesso vale per «Tutti i casi» del riservato); le
 *  tappe sono tutte qui. */
export function IlCasoAperto({ item, caso, servizi }: { item: NewsItem; caso: DatiCaso; servizi: string[] }) {
  if (caso.vista === "notizia" || !caso.cifre) return null;
  const nome = item.occhiello?.trim() || null;
  const parola = caso.vista === "caso" ? "caso" : "report";
  const cifre = [
    { k: "Mesi di osservazione", v: caso.cifre.mesi },
    { k: `Tappe del ${parola}`, v: caso.cifre.tappe },
    { k: "Soggetti mappati", v: caso.cifre.soggetti },
    { k: "Luoghi seguiti", v: caso.cifre.luoghi },
  ];
  return (
    <section
      id="caso"
      aria-labelledby="caso-titolo"
      className="oi-na-caso -mx-[18px] flex scroll-mt-6 flex-col gap-5 px-[18px] pb-16 pt-14 sm:-mx-8 sm:px-8"
    >
      <TestaSezione
        id="caso-titolo"
        titolo={`${ETICHETTA_VISTA[caso.vista]}${nome ? `: ${nome}` : ""}.`}
        sottotitolo={`Come lavoriamo su un ${parola}`}
      />
      <TracciaTappe item={item} caso={caso} />

      <div className="relative mt-4 grid grid-cols-1 overflow-hidden border border-ink border-t-4 border-t-segnale bg-surface lg:grid-cols-2">
        <div className="flex flex-col gap-2.5 p-8 max-md:p-5 lg:pr-11">
          <p className={`m-0 ${ETICHETTA_ACCESA}`}>Cosa possiamo fare per te</p>
          <h3 className="m-0 mb-3 border-b border-ink pb-3 font-serif text-sezione font-normal leading-[1.15] text-ink">
            I nostri servizi
          </h3>
          <div className="grid flex-1 auto-rows-fr grid-cols-1 gap-3.5 sm:grid-cols-2">
            {cardServizi(servizi).map((s) => (
              <article
                key={s.slug}
                className="flex flex-col gap-1.5 border border-rule border-t-2 border-t-mare bg-bg px-5 pb-5 pt-[18px] transition-colors hover:border-t-segnale"
              >
                <div className="flex items-center gap-2.5">
                  <Icona nome={s.icona} className="size-6 text-mare" />
                  <span className={ETICHETTA_ACCESA}>{s.passo}</span>
                </div>
                <h4 className="m-0 mt-0.5 font-serif text-sezione font-normal leading-[1.15] text-ink">{s.titolo}</h4>
                <p className="m-0 font-serif text-lettura italic leading-[1.35] text-ink">{s.domanda}</p>
                <p className="m-0 font-sans text-sommario leading-[1.45] text-sommesso">{s.testo}</p>
                <Link href={`/servizi/${s.slug}`} className={`mt-auto self-start pt-2 ${RIMANDO}`}>
                  {s.invito} <span aria-hidden>↗</span>
                </Link>
              </article>
            ))}
          </div>
        </div>

        <span
          aria-hidden
          className="absolute left-1/2 top-1/2 z-[2] hidden size-16 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full border border-ink bg-bg shadow-[0_0_0_6px_var(--color-surface)] lg:grid"
        >
          <LogoOI variant="segno" decorative accento="fill-segnale" className="h-[22px] w-auto text-mare" />
        </span>

        <div className="flex flex-col gap-2.5 border-ink p-8 max-lg:border-t max-md:p-5 lg:border-l lg:pl-11">
          <p className={`m-0 ${ETICHETTA_ACCESA}`}>Cosa abbiamo fatto su questo {parola}</p>
          <h3 className="m-0 mb-3 border-b border-ink pb-3 font-serif text-sezione font-normal leading-[1.15] text-ink">
            Il lavoro dietro il {parola}
          </h3>
          <dl className="m-0 grid grid-cols-2 border-b border-ink">
            {cifre.map((c, i) => (
              <div
                key={c.k}
                className={`flex items-baseline gap-3.5 border-rule py-[26px] max-md:flex-col max-md:gap-1 ${
                  i % 2 === 0 ? "border-r pr-5" : "pl-5"
                } ${i < 2 ? "border-b" : ""}`}
              >
                <dt className="order-2 font-sans text-sommario leading-[1.3] text-sommesso">{c.k}</dt>
                <dd
                  className={`order-1 m-0 min-w-16 font-serif text-apertura leading-none tabular-nums ${
                    i === 1 ? "text-segnale" : "text-mare"
                  }`}
                >
                  {c.v}
                </dd>
              </div>
            ))}
          </dl>
          <p className={`m-0 pt-4 ${ETICHETTA}`}>Il metodo, passo per passo</p>
          <ol className="m-0 list-none p-0 pt-1">
            {METODO.map((m, i) => {
              const ultimo = i === METODO.length - 1;
              return (
                <li
                  key={m.n}
                  className={`grid grid-cols-[44px_minmax(0,1fr)] items-baseline gap-x-3 gap-y-1 py-[21px] md:grid-cols-[44px_150px_minmax(0,1fr)] ${
                    i > 0 ? "border-t border-rule" : ""
                  }`}
                >
                  <span
                    aria-hidden
                    className={`border-t-2 pt-1 font-mono text-meta font-semibold ${
                      ultimo ? "border-segnale text-segnale" : "border-mare text-mare"
                    }`}
                  >
                    {m.n}
                  </span>
                  <b className="font-serif text-[20px] font-normal leading-[1.2] text-ink">{m.titolo}</b>
                  <p className="m-0 font-sans text-sommario leading-[1.4] text-sommesso max-md:col-start-2">{m.testo}</p>
                </li>
              );
            })}
          </ol>
        </div>

        <p className="col-span-full m-0 flex flex-wrap items-center justify-between gap-3 border-t border-ink bg-bg px-8 py-4 font-serif text-[20px] text-ink max-md:px-5">
          <span>Un {parola} costruito sulla tua filiera, sotto NDA.</span>
          <Link href="/contatti" className={RIMANDO}>
            Parliamone <span aria-hidden>↗</span>
          </Link>
        </p>
      </div>
    </section>
  );
}

/* ── Per approfondire e i rulli ─────────────────────────────────── */

/** La riga dei dati di una scheda: il puntino del tema, il Paese in
 *  grassetto, la rubrica (o il tipo, se la rubrica è vuota) e la data. */
function RigaDati({
  territorio,
  categoria,
  tipo,
  data,
  segno = "punto",
}: {
  territorio: string | null;
  categoria: string;
  tipo?: string;
  data?: { iso: string; testo: string };
  segno?: "punto" | "icona" | "nessuno";
}) {
  const tono = tonoRubrica(categoria);
  const voci: ReactNode[] = [];
  if (territorio) voci.push(<b key="t" className="font-semibold text-ink">{etichettaLuogo(territorio)}</b>);
  if (categoria || tipo) voci.push(<span key="c">{categoria || tipo}</span>);
  if (data)
    voci.push(
      <time key="d" dateTime={data.iso}>
        {data.testo}
      </time>
    );
  return (
    <span className={`flex flex-wrap items-center gap-x-1.5 gap-y-0.5 ${DATI_MONO}`}>
      {segno === "punto" ? <span aria-hidden className="size-[7px] rounded-full" style={{ background: tono }} /> : null}
      {segno === "icona" && categoria ? <Icona nome={iconaCategoria(categoria)} tono={tono} className="size-4" /> : null}
      {voci.map((v, i) => (
        <span key={i} className="inline-flex items-center gap-1.5">
          {i > 0 ? <span aria-hidden>·</span> : null}
          {v}
        </span>
      ))}
    </span>
  );
}

/** «Per approfondire» (`.n-corr`): tre dispacci dello stesso territorio,
 *  degli stessi soggetti o dello stesso tema (`approfondimentiPer`), foto
 *  16:9, riga dei dati e titolo serif 20. Tutta la scheda è il
 *  collegamento (il titolo si allunga su tutta la scatola). */
export function PerApprofondireAperto({ voci, paese }: { voci: Approfondimento[]; paese: string | null }) {
  if (voci.length === 0) return null;
  return (
    <section aria-labelledby="approfondire">
      <TestaSezione
        id="approfondire"
        titolo="Per approfondire."
        sottotitolo="Stesso territorio, stessi soggetti, stesso tema"
        rimando={paese ? { href: hrefPaese(paese), etichetta: `Tutto su ${etichettaLuogo(paese)}` } : undefined}
      />
      <ul className="m-0 grid list-none grid-cols-1 gap-6 p-0 md:grid-cols-3">
        {voci.map((a) => (
          <li key={a.id} className="flex min-w-0">
            <article className="group/c relative flex w-full flex-col overflow-hidden rounded-[3px] border border-rule bg-surface">
              {a.foto ? (
                <span className="relative block aspect-video w-full overflow-hidden bg-surface2">
                  <Image
                    src={a.foto}
                    alt=""
                    fill
                    sizes="(min-width: 1440px) 440px, (min-width: 768px) 31vw, 100vw"
                    className="object-cover"
                  />
                </span>
              ) : null}
              <div className="flex flex-col gap-2 px-5 pb-5 pt-[18px]">
                <RigaDati
                  territorio={a.territorio}
                  categoria={a.categoria}
                  tipo={PRIORITY_MAP[a.tipo]?.label}
                  data={a.data}
                />
                <h3 className="m-0 font-serif text-[20px] font-normal leading-[1.3]">
                  <Link
                    href={a.href}
                    className={`text-ink no-underline transition-colors after:absolute after:inset-0 group-hover/c:text-mare ${FUOCO}`}
                  >
                    {a.titolo}
                  </Link>
                </h3>
              </div>
            </article>
          </li>
        ))}
      </ul>
    </section>
  );
}

function VoceElenco({ v, segno }: { v: VoceRullo; segno: "icona" | "nessuno" }) {
  return (
    <li className="border-t border-rule first:border-t-0">
      <Link href={v.href} className={`group/v flex flex-col gap-1 py-3 text-ink no-underline ${FUOCO}`}>
        <RigaDati
          territorio={v.territorio}
          categoria={segno === "icona" ? v.categoria : ""}
          data={v.data}
          segno={segno}
        />
        <span className="font-serif text-lettura leading-[1.35] transition-colors group-hover/v:text-mare">{v.titolo}</span>
      </Link>
    </li>
  );
}

/** La fascia `.n-grande`: due schede grandi dello stesso Paese e dello
 *  stesso tema (il «Finanziamenti in Serbia · dove vanno i soldi» del
 *  modello), poi i segnali deboli e le altre notizie sul Paese, e il
 *  piede coi due rimandi. Il tema è quello di redazione quando a monte
 *  c'è (`rulli.temaRedazione`, oggi mai), altrimenti la rubrica di monte:
 *  vedi `rulliPer`. Ogni parte si rende solo se ha voci vere; senza
 *  nessuna, niente fascia. */
export function RulliAperti({ rulli, paese }: { rulli: RulliNotizia | null; paese: string | null }) {
  if (!rulli) return null;
  const { grandi, altre, segnali } = rulli;
  if (grandi.length + altre.length + segnali.length === 0) return null;
  const nomePaese = paese ? etichettaLuogo(paese) : null;
  const tema = rulli.temaRedazione;
  const rubrica = rulli.tema ? grandi[0]?.categoria : "";

  /* Col tema di redazione titolo e occhiello sono quelli del modello. Il
     titolo va coi due punti e non con «in»: «in Serbia» ma «negli Stati
     Uniti», «nel Regno Unito», e la preposizione giusta a monte non c'è. */
  const occhiello = nomePaese
    ? `${nomePaese} · ${tema ? (tema.occhiello ?? tema.nome.toLocaleLowerCase("it")) : rulli.tema ? "stesso tema" : "dal rullo"}`
    : "Dal rullo dell’Osservatorio";
  const titolo = nomePaese
    ? tema
      ? `${tema.nome}: ${nomePaese}.`
      : rulli.tema && rubrica
        ? `${nomePaese}: ${rubrica.toLocaleLowerCase("it")}.`
        : `${nomePaese}: le ultime notizie.`
    : "I segnali deboli.";
  const motto = nomePaese
    ? tema?.descrizione
      ? tema.descrizione
      : rulli.tema
        ? "Le ultime notizie su questo Paese e questo tema."
        : "Le ultime notizie su questo Paese, seguite dall’Osservatorio."
    : "Le anomalie che vengono prima delle notizie.";

  const colonne = [altre.length > 0, segnali.length > 0].filter(Boolean).length;

  return (
    <section aria-labelledby="rulli-titolo" className="border border-ink border-t-4 border-t-segnale bg-surface">
      <header className="flex flex-wrap items-end justify-between gap-x-8 gap-y-3 border-b border-ink px-8 pb-[22px] pt-7 max-md:px-5">
        <div>
          <p className={`m-0 mb-1.5 ${ETICHETTA_ACCESA}`}>{occhiello}</p>
          <h2 id="rulli-titolo" className="m-0 font-serif text-fascia font-normal leading-[1.15] text-ink">
            <SegnoTitolo />
            {titolo}
          </h2>
        </div>
        <p className="m-0 font-serif text-[20px] italic text-sommesso">{motto}</p>
      </header>

      {grandi.length > 0 ? (
        <div className={`grid grid-cols-1 border-b border-ink ${grandi.length > 1 ? "lg:grid-cols-2" : ""}`}>
          {grandi.map((v, i) => (
            <Link
              key={v.id}
              href={v.href}
              className={`group/g flex flex-col text-ink no-underline ${i > 0 ? "border-ink max-lg:border-t lg:border-l" : ""} ${FUOCO}`}
            >
              {v.foto ? (
                <span className="relative block aspect-[2/1] w-full overflow-hidden bg-surface2">
                  <Image
                    src={v.foto}
                    alt=""
                    fill
                    sizes={grandi.length > 1 ? "(min-width: 1440px) 690px, (min-width: 1024px) 48vw, 100vw" : "(min-width: 1440px) 1376px, 100vw"}
                    className="object-cover"
                  />
                </span>
              ) : null}
              <span className="flex flex-1 flex-col gap-2.5 px-7 pb-[26px] pt-[22px] max-md:px-5">
                <RigaDati territorio={v.territorio} categoria={v.categoria} tipo={PRIORITY_MAP[v.tipo]?.label} data={v.data} segno="icona" />
                <span className="font-serif text-fascia leading-[1.15] transition-colors group-hover/g:text-mare">{v.titolo}</span>
                {v.sommario ? (
                  <span className="line-clamp-3 font-sans text-sommario leading-[1.45] text-sommesso">{v.sommario}</span>
                ) : null}
                <span className="mt-auto pt-1 font-sans text-corpo font-semibold text-mare">
                  Leggi la notizia <span aria-hidden>↗</span>
                </span>
              </span>
            </Link>
          ))}
        </div>
      ) : null}

      {colonne > 0 ? (
        <div className={`grid grid-cols-1 ${colonne === 2 ? "lg:grid-cols-2" : ""}`}>
          {segnali.length > 0 ? (
            <div className="oi-na-segnali px-7 pb-6 pt-[22px] max-md:px-5">
              <p className={`m-0 mb-1.5 flex items-center gap-2 ${ETICHETTA_ACCESA}`}>
                <Icona nome="segnale" className="size-5" />
                Segnali deboli
              </p>
              <ul className="m-0 list-none p-0">
                {segnali.map((v) => (
                  <VoceElenco key={v.id} v={v} segno="nessuno" />
                ))}
              </ul>
            </div>
          ) : null}
          {altre.length > 0 ? (
            <div
              className={`bg-bg px-7 pb-6 pt-[22px] max-md:px-5 ${segnali.length > 0 ? "border-ink max-lg:border-t lg:border-l" : ""}`}
            >
              <p className={`m-0 mb-1.5 flex items-center gap-2 ${ETICHETTA_ACCESA}`}>
                <Icona nome="notizia" className="size-5" />
                {nomePaese ? `Altre notizie: ${nomePaese}` : "Altre notizie"}
              </p>
              <ul className="m-0 list-none p-0">
                {altre.map((v) => (
                  <VoceElenco key={v.id} v={v} segno="icona" />
                ))}
              </ul>
            </div>
          ) : null}
        </div>
      ) : null}

      <footer className={`grid grid-cols-1 border-t border-ink ${paese ? "lg:grid-cols-2" : ""}`}>
        {paese ? (
          <Link
            href={hrefPaese(paese)}
            className={`flex items-baseline justify-between gap-4 px-8 py-4 font-sans text-corpo font-semibold text-mare no-underline hover:underline max-md:px-5 ${FUOCO}`}
          >
            <span>
              Tutte le notizie: {nomePaese} <span aria-hidden>↗</span>
            </span>
            {rubrica ? <small className="text-right font-sans text-voce font-normal text-sommesso">{rubrica}</small> : null}
          </Link>
        ) : null}
        <Link
          href="/segnali-deboli"
          className={`flex items-baseline justify-between gap-4 border-ink px-8 py-4 font-sans text-corpo font-semibold text-mare no-underline hover:underline max-md:px-5 ${paese ? "max-lg:border-t lg:border-l" : ""} ${FUOCO}`}
        >
          <span>
            Tutti i segnali deboli <span aria-hidden>↗</span>
          </span>
          {rulli.segnaliTotale ? (
            <small className="text-right font-sans text-voce font-normal tabular-nums text-sommesso">
              {rulli.segnaliTotale.toLocaleString("it-IT")} in archivio
            </small>
          ) : null}
        </Link>
      </footer>
    </section>
  );
}
